import React, { useEffect, useMemo, useState } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import Sidebar from '../../components/Sidebar/Sidebar';
import { useData } from '../../context/DataContext';
import RenderConsole from './RenderConsole';
import './SA_ControlCenter.css';
import './SA_Logs.css';

import { Search, FileText } from 'lucide-static';

const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;

    if (size) {
      svgString = svgString.replace('<svg', `<svg width="${size}" height="${size}"`);
    }

    if (color) {
      svgString = svgString.replace('<svg', `<svg color="${color}" stroke="${color}"`);
    }

    if (className) {
      svgString = svgString.replace('<svg', `<svg class="${className}"`);
    }

    return svgString;
  };

  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

const SA_Logs = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [runtimeTarget, setRuntimeTarget] = useState('mobile');
  const LOGS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const { logs: contextLogs, logsLoaded, refreshLogs } = useData();
  const [logs, setLogs] = useState(contextLogs);
  const [loadingLogs, setLoadingLogs] = useState(!logsLoaded);
  const [logsError, setLogsError] = useState('');

  useEffect(() => {
    setLogs(contextLogs);
    if (logsLoaded) {
      setLoadingLogs(false);
    }
  }, [contextLogs, logsLoaded]);

  const formatRelativeTime = (timeMs) => {
    if (!timeMs || Number.isNaN(timeMs)) return 'Unknown time';

    const diff = Date.now() - timeMs;
    if (diff < 60 * 1000) return 'Just now';

    const mins = Math.floor(diff / (60 * 1000));
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;

    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;

    return new Date(timeMs).toLocaleString();
  };

  const decorateSeverity = (severity) => {
    const normalized = String(severity || '').toLowerCase();
    if (normalized === 'warning') return 'warning';
    if (normalized === 'high' || normalized === 'critical' || normalized === 'error') return 'high';
    return 'info';
  };

  const filteredLogs = useMemo(() => {
    return logs
      .filter((log) => String(log.action || '').toLowerCase() !== 'page viewed')
      .filter((log) => {
        return [
        log.action,
        log.actor,
        log.details,
        log.ip,
        log.severity,
        log.category,
        log.path,
        ]
          .join(' ')
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
  }, [logs, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / LOGS_PER_PAGE));

  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * LOGS_PER_PAGE;
    return filteredLogs.slice(start, start + LOGS_PER_PAGE);
  }, [filteredLogs, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, logs]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="dashboard-container">
      <Sidebar />

      <main className="main-content">
        <div className="logs-top-bar">
          <div className="logs-search-section">
            <div className="logs-search-wrapper">
              <Icon icon={Search} size={18} className="logs-search-icon" />
              <input
                type="text"
                placeholder="Search Logs..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchInput);
                  }
                }}
                className="logs-search-input"
              />
            </div>
          </div>
        </div>

        <div className="logs-card">
          <h1 className="card-title">System Logs</h1>
          <p className="card-subtitle">Track important system events, actions, and audit entries</p>

          <div className="logs-list">
            {loadingLogs ? (
              <div className="no-logs-state">
                <Icon icon={FileText} size={48} className="no-logs-icon" />
                <span>Loading audit logs...</span>
              </div>
            ) : logsError ? (
              <div className="no-logs">{logsError}</div>
            ) : filteredLogs.length > 0 ? (
              paginatedLogs.map((log, index) => (
                <div key={log.id || index} className="log-card">
                  <div className="log-left">
                    <Icon icon={FileText} size={24} className="log-icon" />
                    <div className="log-text">
                      <div className="log-title">
                        {log.action}
                        <span className={`severity-pill severity-${decorateSeverity(log.severity)}`}>
                          {log.severity}
                        </span>
                      </div>
                      <div className="log-details">{log.details}</div>
                    </div>
                  </div>
                  <div className="log-time">{formatRelativeTime(log.timeMs)}</div>
                </div>
              ))
            ) : (
              <div className="no-logs">No logs found matching your search.</div>
            )}
          </div>

          {!loadingLogs && !logsError && filteredLogs.length > 0 && (
            <div className="logs-pagination">
              <span className="logs-pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <div className="logs-pagination-actions">
                <button
                  type="button"
                  className="logs-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="logs-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        <RenderConsole target={runtimeTarget}>
          <div className="cc-service-picker" aria-label="Runtime log service">
            {['mobile', 'admin'].map(service => <button key={service} type="button"
              className={`cc-service-choice ${runtimeTarget === service ? 'cc-service-choice-active' : ''}`}
              aria-pressed={runtimeTarget === service} onClick={() => setRuntimeTarget(service)}>
              <span className="cc-service-name">{service === 'mobile' ? 'Mobile App Backend' : 'Admin Dashboard Backend'}</span>
            </button>)}
          </div>
        </RenderConsole>
      </main>
    </div>
  );
};

export default SA_Logs;
